import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import InputMask from 'react-input-mask';
import Confetti from 'react-confetti';
import Navbar from '../components/Navbar';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import Footer from '../components/Footer';
import { getClickIds, getFunnel, cameFromVariant } from '../utils/tracking';
import { resolveVariant, logOfferEvent, currentVariant } from '../utils/offerTest';
import injectCollectJS from '../injectCollect';
import { cardFieldsComplete } from '../utils/cardValidity';
import { isVariantHost, mainUrl, VARIANT_HOSTS, VARIANT_SITE } from '../utils/variant';
import { packageFor, QUALITY_TIERS, DEFAULT_QUALITY, qualityPrice, addonTotalFor, addonPriceFor } from '../config/packages';
import { useLanguage } from '../contexts/LanguageContext';

// One-page 50%-off funnel: ad click -> plan cards -> single-screen checkout.
// No account/profile steps before payment; the profile is linked in the same
// submit and validated after the charge.

// The 2× growth upsell is priced against the package it attaches to — half of
// it — rather than a flat figure. Mirrored in routes/purchase.js UPSELL_PRICES.
//
// Half a price ending in .99 lands on a half-cent (9.99 / 2 = 4.995), so the
// cent is dropped rather than rounded: half of $9.99 is $4.99, not $5.00. Going
// through whole cents first keeps it off binary floating point, where
// 9.99 * 50 is not exactly 499.5.
const halfOf = (n) => Math.floor(Math.round(n * 100) / 2) / 100;

// Mirrors routes/purchase.js ENGAGEMENT_PACKAGES. The post-purchase offer is
// sized to the package the customer just bought.
const ENGAGEMENT_PACKAGES = {
	starter: { price: 2.98, likes: 500, views: 1000 },
	basic: { price: 5.98, likes: 2000, views: 5000 },
	pro: { price: 9.98, likes: 5000, views: 10000 },
};

// Auto-refill plan. Mirrors REFILL_PLAN in routes/purchase.js — this is only
// what the disclosure quotes; the server charges from its own copy.
const REFILL_PRICE = 19.99;
const REFILL_TRIAL_DAYS = 7;
const REFILL_CYCLE_DAYS = 28;

// The 'engagement' upsell adds half the package again for half its price — the
// same rate per follower the package sells at. The offer states the count,
// rather than a "2× growth" label the buyer has to translate. Mirrored by
// UPSELL_DELIVERY.engagement in routes/purchase.js, which sends this quantity.
const oneTimeFollowers = (key) => Math.round(packageFor(key).followers / 2).toLocaleString();

const buildPlans = (T) => ({
	starter: {
		name: T.planMicro,
		badge: '10k',
		// Numbers come from config/packages.js so /pricing and this funnel can
		// never advertise different prices for the same package key.
		anchor: packageFor('starter').anchor,
		price: packageFor('starter').price,
		followers: '200',
		monthlyGrowth: packageFor('starter').followers,
		cta: T.startGrowing,
		summary: [T.summaryFollowersStarter, T.featGrowthStarts, T.featStarterTargeting],
		features: [
			[T.featStarterFollowers, true],
			[T.featGrowthStarts, false],
			[T.featStarterTargeting, false],
		],
		// Opt-in, not pre-checked (maps to backend 'engagement')
		// The offer is the follower count it adds, not a growth adjective: the
		// key delivers half the package again (routes/purchase.js
		// UPSELL_DELIVERY.engagement).
		defaultUpsells: [],
		upsellOptions: [
			{ key: 'engagement', label: T.upgradeFollowers(oneTimeFollowers('starter')), lineLabel: T.upgradeFollowersLine(oneTimeFollowers('starter')), price: halfOf(packageFor('starter').price) },
		],
	},
	basic: {
		name: T.planInfluencer,
		badge: '500k',
		anchor: packageFor('basic').anchor,
		price: packageFor('basic').price,
		followers: '1,200',
		monthlyGrowth: packageFor('basic').followers,
		cta: T.startGrowing,
		summary: [T.summaryFollowersBasic, T.featGrowthStarts, T.featStarterTargeting],
		popular: true,
		features: [
			[T.featBasicFollowers, true],
			[T.featGrowthStarts, false],
			[T.featBasicTargeting, false],
			[T.featBasicTools, false],
			[T.featBasicEngagement, false],
		],
		// Opt-in, not pre-checked (maps to backend 'engagement')
		defaultUpsells: [],
		upsellOptions: [
			{ key: 'engagement', label: T.upgradeFollowers(oneTimeFollowers('basic')), lineLabel: T.upgradeFollowersLine(oneTimeFollowers('basic')), price: halfOf(packageFor('basic').price) },
		],
	},
	pro: {
		name: T.planBusiness,
		badge: null,
		anchor: packageFor('pro').anchor,
		price: packageFor('pro').price,
		followers: '4,000',
		monthlyGrowth: packageFor('pro').followers,
		cta: T.startGrowing,
		summary: [T.summaryFollowersPro, T.featGrowthStarts, T.featStarterTargeting],
		features: [
			[T.featProFollowers, true],
			[T.featGrowthStarts, false],
			[T.featProTargeting, false],
			[T.featProManager, false],
			[T.featProPriority, false],
		],
		// Top tier: custom optional upsells, none pre-checked
		defaultUpsells: [],
		// 'likes' retired from checkout — auto-likes now ship with every plan.
		upsellOptions: [
			{ key: 'views', label: T.boostEngagement, sub: T.boostEngagementSub, price: halfOf(packageFor('pro').price) },
		],
	},
});

// Mirrors the picker in components/Visualizer.js — icon only, no labels.
const PLATFORMS = [
	{ id: 'instagram', icon: 'fa-instagram', color: '#E4405F' },
	{ id: 'tiktok', icon: 'fa-tiktok', color: '#000' },
	{ id: 'x', icon: 'fa-x-twitter', color: '#000' },
];

const PLAN_ORDER = ['pro', 'basic', 'starter'];

// The shared stripe-* type scale is desktop-sized; scale it down on phones so
// the hero doesn't wrap to four lines.
const RESPONSIVE_CSS = `
/* Visualizer defines this too, but in its own <style> — which never renders here. */
@keyframes fadeSlideIn {
	from { opacity: 0; transform: translateY(16px); }
	to { opacity: 1; transform: translateY(0); }
}
@media (max-width: 640px) {
	.fiftyoff-hero h1 {
		font-size: 1.85rem !important;
		line-height: 1.32 !important;
		letter-spacing: -0.005em !important;
		margin-bottom: 1rem !important;
	}
	.fiftyoff-hero p { font-size: 0.95rem !important; line-height: 1.65 !important; }
	.fiftyoff-guarantee h2 { font-size: 1.6rem !important; line-height: 1.3 !important; }
	.fiftyoff-guarantee p { font-size: 0.95rem !important; line-height: 1.65 !important; }
	.fiftyoff-banner { font-size: 0.82rem !important; padding: 0.55rem 0.75rem !important; }
}
`;

const GuaranteeBlock = ({ T }) => (
	<section className="stripe-section--gradient" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
		<div className="fiftyoff-guarantee stripe-container px-4 text-center" style={{ maxWidth: 720 }}>
			<h2 className="stripe-heading stripe-heading--lg mb-3">{T.guaranteeHeading}</h2>
			<p className="stripe-body stripe-body--lg stripe-text-muted mx-auto" style={{ maxWidth: 560, marginBottom: 0 }}>
				{T.guaranteeBody}<strong style={{ color: '#0a2540' }}>{T.guaranteeBold}</strong>
			</p>
		</div>
	</section>
);

// Add-on prices come from config/packages.js, per package — the backend charges
// from its own copy of the same table.

// 4.99 has no exact binary representation, so any sum touching the add-ons
// drifts (29 + 2 * 4.99 === 38.980000000000004). Round before displaying or
// charging anything.
const round2 = (n) => Math.round(n * 100) / 100;
// Whole dollars stay bare ($29) — matching the plan cards; cents show in full.
const money = (n) => (Number.isInteger(round2(n)) ? String(round2(n)) : round2(n).toFixed(2));

// The visualizer's paid switches arrive as bare upsell keys, so the order
// summary resolves them back to labelled line items — without this they'd be
// folded into the total with nothing on screen to explain the difference.
const addonLines = (keys, L, planKey) => {
	const labels = {
		fast: L.visualizer.speedFast,
		female: L.visualizer.femaleOnlyTitle,
		usa: L.fiftyoff.addonUsa,
	};
	// Taking all three is a bundle rather than a sum, so the individual prices
	// are shown as line items and the bundle discount is left to the total —
	// itemising a discount nobody asked about only invites arithmetic.
	return keys.filter((k) => labels[k]).map((k) => ({ key: k, label: labels[k], price: addonPriceFor(planKey, k) }));
};

// Apple-style on/off switch, sitting at the right edge of the upsell rows and
// the auto-refill box. Same dimensions as the visualizer's AddonSwitch so the
// funnel reads as one control language. stopPropagation because the row it
// lives in is itself clickable — without it every tap toggles twice.
const Switch = ({ checked, onChange, label }) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={label}
		onClick={(e) => { e.stopPropagation(); onChange(); }}
		style={{
			width: 44, height: 26, padding: 3, border: 'none', borderRadius: 999, cursor: 'pointer', flexShrink: 0,
			background: checked ? '#635bff' : '#c9d2e0', transition: 'background 0.2s ease',
		}}
	>
		<span style={{
			display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff',
			transform: checked ? 'translateX(18px)' : 'translateX(0)', transition: 'transform 0.2s ease',
		}}></span>
	</button>
);

// Affiliate attribution: ?r= wins, else the cookie LanguageProvider persisted.
// Sent in the body as well as via the cookie so attribution survives a blocked
// or not-yet-written cookie.
// Turns the visualizer's stored choices back into display chips. The stored
// values are the profile-record shapes (both/men/women, us/global/<cc>), not the
// picker ids, so this is where they're mapped back to labels.
const prefChips = (prefs, L) => {
	const V = L.visualizer;
	const speed = {
		turbo: { icon: '⚡', label: V.speedTurbo },
		fast: { icon: '🚀', label: V.speedFast },
		gradual: { icon: '🌱', label: V.speedGradual },
	}[prefs.deliverySpeed];
	const audience = {
		both: { icon: '👫', label: V.audienceEveryone },
		men: { icon: '👨', label: V.audienceMen },
		women: { icon: '👩', label: V.audienceWomen },
	}[prefs.genderTarget];
	// Anything that isn't us/global is a specific country picked from the list,
	// whose label already carries its own flag.
	const location =
		prefs.countries === 'us'
			? { icon: '🇺🇸', label: V.locationUsa }
			: prefs.countries === 'global'
				? { icon: '🌍', label: V.locationWorldwide }
				: L.magnet.countries[prefs.countries]
					? { icon: '📍', label: L.magnet.countries[prefs.countries] }
					: { icon: '🌍', label: V.locationWorldwide };

	return [
		speed && { key: 'speed', ...speed },
		audience && { key: 'audience', ...audience },
		location && { key: 'location', ...location },
	].filter(Boolean);
};

const getAffiliate = () => {
	const r = new URLSearchParams(window.location.search).get('r');
	if (r) return r;
	return document.cookie.split('; ').find((c) => c.startsWith('affiliate='))?.split('=')[1] || '';
};

const firePixel = (event, extra = {}) => {
	const affiliate = getAffiliate();
	axios.post('https://api.viralgpt.com/pixel', { event, funnel: getFunnel(), ...getClickIds(), ...(affiliate ? { affiliate } : {}), ...extra }, { withCredentials: true }).catch(() => {});
};

const FiftyOff = () => {
	const L = useLanguage();
	const T = L.fiftyoff;
	// Offer test: which tiers this visitor sees. null until the API answers,
	// which is also what counts them as a visitor — so the plan grid renders the
	// full menu meanwhile and never flashes a tier away.
	const [offerTiers, setOfferTiers] = useState(null);
	// Per-pack price overrides for the assigned arm. routes/purchase.js applies
	// the same table, so the charge agrees with what is rendered here.
	const [offerPrices, setOfferPrices] = useState(null);

	const basePlans = React.useMemo(() => buildPlans(T), [T]);
	// Rebuilt rather than mutated so a re-render never compounds the override.
	const PLANS = React.useMemo(() => {
		if (!offerPrices) return basePlans;
		const out = { ...basePlans };
		for (const [key, price] of Object.entries(offerPrices)) {
			if (out[key]) out[key] = { ...out[key], price };
		}
		return out;
	}, [basePlans, offerPrices]);
	const [view, setView] = useState('plans');
	const [plan, setPlan] = useState('basic');
	// Quality is chosen after the quantity; it multiplies whatever that costs.
	const [quality, setQuality] = useState(DEFAULT_QUALITY);
	// Auto-refill, pre-checked. Sent with the purchase and recorded server-side.
	const [autoRefill, setAutoRefill] = useState(false);
	const [upsells, setUpsells] = useState([]);
	const [email, setEmail] = useState('');
	const [platform, setPlatform] = useState('instagram');
	const [username, setUsername] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);
	// Whether the account already has a real password, per the purchase
	// response. The success screen offers to set one only when there is none.
	const [hasPassword, setHasPassword] = useState(false);
	// Post-purchase engagement add-on, sold on the success screen.
	const [engagementLoading, setEngagementLoading] = useState(false);
	const [engagementDone, setEngagementDone] = useState(false);
	const [engagementError, setEngagementError] = useState('');
	// How the package itself was paid for. A wallet charge stores no PAN, so
	// these buyers have no row in `cards` and no profiles.cardid — the one-click
	// add-on has nothing to charge and can only ever fail. They take the offer
	// through the wallet buttons instead.
	const [paidWithWallet, setPaidWithWallet] = useState(false);
	// The server's own answer for which pack this buyer earned. The local table
	// below is only a first guess for render; this is what the wallet sheet is
	// authorized against, because the sheet amount and the charge amount have to
	// be the same number or NMI refuses the sale.
	const [engagementOffer, setEngagementOffer] = useState(null);
	const [secondsLeft, setSecondsLeft] = useState(0);
	const cardRef = useRef();
	const expRef = useRef();
	const cvvRef = useRef();
	const paymentInfoFired = useRef(false);
	const checkoutStartedRef = useRef(false);
	// Account creation and profile linking both happen on Continue — nothing is
	// written while the visitor is still typing.
	const authRef = useRef(null);          // { token, email }
	const linkedRef = useRef(null);        // { profileid, platform, username }
	const queueRef = useRef(Promise.resolve()); // serialize capture calls
	const mountedRef = useRef(true);
	const [isReinitializingCollect, setIsReinitializingCollect] = useState(false);
	// Single-screen checkout: the account is created in the background as soon as
	// a valid email is typed, so there is no Continue step. Identity collapses to
	// a summary row when the visualizer already told us who to grow.
	const [editingIdentity, setEditingIdentity] = useState(true);
	// Scanned profile behind the gain widget. Fetched after Continue and rendered
	// when it lands — the card fields never wait on it.
	const [gainProfile, setGainProfile] = useState(null);
	// What the visitor configured in the visualizer (speed / audience / location).
	// Null when they arrived without going through it — the card then shows the
	// plan summary alone, as before.
	const [growthPrefs, setGrowthPrefs] = useState(null);
	// Paid add-ons chosen on the customize screen ('fast' / 'female' / 'usa').
	// Kept separate from `upsells` so the plan's own opt-ins stay independent,
	// then merged when the purchase is sent.
	const [growthAddons, setGrowthAddons] = useState([]);

	useEffect(() => {
		let alive = true;
		resolveVariant().then((v) => {
			if (!alive) return;
			if (v.tiers) setOfferTiers(v.tiers);
			if (v.prices && Object.keys(v.prices).length) setOfferPrices(v.prices);
		});
		return () => { alive = false; };
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const preselected = params.get('plan');
		if (preselected && PLANS[preselected]) {
			setPlan(preselected);
			setUpsells(PLANS[preselected].defaultUpsells);
			setView('checkout');
			// The plan was picked on the visualizer, so the choice already happened
			// upstream — this is the first page we control, so fire it here.
			checkoutStartedRef.current = true;
			firePixel('InitiateCheckout', { price: PLANS[preselected].price });
			// The offer test's checkout step, same as selectPlan logs on the other
			// path. Without it the arms table showed zero checkouts against real
			// submits, because most visitors arrive here with a plan already chosen
			// on the visualizer and never touch selectPlan.
			logOfferEvent('checkout', { pack: preselected });

			// Reuse the profile the visualizer already scanned so the visitor
			// doesn't retype it. Ignore stale handoffs from an old session.
			try {
				const raw = localStorage.getItem('visualizer_handoff');
				if (raw) {
					const h = JSON.parse(raw);
					const fresh = h?.ts && (Date.now() - h.ts) < 60 * 60 * 1000;
					if (fresh && h.username) {
						setUsername(h.username);
						const plat = ['instagram', 'tiktok', 'x'].includes(h.platform) ? h.platform : 'instagram';
						setPlatform(plat);
						// Already known — show it as a summary with a Change button
						// rather than asking for it a second time.
						setEditingIdentity(false);
						fetchGain(h.username, plat);
					}
					// Quality is chosen on the visualizer's step 2, so checkout takes
					// it from the handoff rather than asking again.
					if (fresh && QUALITY_TIERS.some((t) => t.key === h.quality)) {
						setQuality(h.quality);
					}
					if (fresh && Array.isArray(h.addons)) {
						setGrowthAddons(h.addons.filter((a) => ['fast', 'female', 'usa'].includes(a)));
					}
					if (fresh && (h.deliverySpeed || h.genderTarget || h.countries)) {
						setGrowthPrefs({
							deliverySpeed: h.deliverySpeed || 'turbo',
							genderTarget: h.genderTarget || 'both',
							countries: h.countries || 'global',
						});
					}
				}
			} catch (e) { /* malformed handoff — visitor just types it in */ }
		} else {
			firePixel('home_view');
			firePixel('ViewContent');
		}
	}, []);

	// Offer expires at midnight in the visitor's own timezone.
	useEffect(() => {
		const nextMidnight = () => {
			const d = new Date();
			d.setHours(24, 0, 0, 0);
			return d.getTime();
		};
		let deadline = nextMidnight();
		const tick = () => {
			// Roll over if the clock passes midnight while the page is open.
			if (Date.now() >= deadline) deadline = nextMidnight();
			setSecondsLeft(Math.max(0, Math.floor((deadline - Date.now()) / 1000)));
		};
		tick();
		const id = setInterval(tick, 1000);
		return () => clearInterval(id);
	}, []);

	const p = PLANS[plan];
	// Server answer wins. `plan` is the tier this session selected, which is
	// normally the tier the server resolves too — but the server reads the
	// checkout that actually cleared, and only it can be right about what was
	// bought. Falling back to the local table keeps the offer renderable if the
	// lookup fails; it is no worse than the guess we used to ship.
	const postPurchaseEngagement = engagementOffer || ENGAGEMENT_PACKAGES[plan] || ENGAGEMENT_PACKAGES.basic;
	const upsellTotal = p.upsellOptions.filter((u) => upsells.includes(u.key)).reduce((s, u) => s + u.price, 0);
	// The customize screen's paid switches arrive via the handoff rather than the
	// plan's own upsell list, but they're the same kind of line item.
	const addonItems = addonLines(growthAddons, L, plan);
	const addonTotal = addonTotalFor(plan, growthAddons);
	const tierPrice = qualityPrice(p.price, quality);
	const tierAnchor = qualityPrice(p.anchor, quality);
	const total = round2(tierPrice + upsellTotal + addonTotal);

	// InitiateCheckout = the visitor chose a package.
	const selectPlan = (key) => {
		if (!checkoutStartedRef.current) {
			checkoutStartedRef.current = true;
			firePixel('InitiateCheckout', { price: PLANS[key].price });
			logOfferEvent('checkout', { pack: key });
		}
		if (isVariantHost) {
			window.location.href = mainUrl(`/50off?plan=${key}&from=start`);
			return;
		}
		setPlan(key);
		setUpsells(PLANS[key].defaultUpsells);
		setError('');
		setView('checkout');
		window.scrollTo(0, 0);
	};

	const toggleUpsell = (key) => {
		setUpsells((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	};

	const cleanUsername = (raw) => {
		let u = raw.trim().replace(/^@/, '');
		// Accept pasted profile URLs
		const urlMatch = u.match(/(?:instagram\.com|tiktok\.com|x\.com|twitter\.com)\/(@?[A-Za-z0-9._]+)/i);
		if (urlMatch) u = urlMatch[1].replace(/^@/, '');
		return u;
	};

	const platformLabel = platform === 'x' ? 'X' : platform.charAt(0).toUpperCase() + platform.slice(1);

	// Create the passwordless account as soon as a valid email is entered.
	const ensureRegistered = async () => {
		const value = email.trim();
		if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return null;
		if (authRef.current?.email === value) return authRef.current;

		// A changed email means a different account and a different token, so any
		// profile linked under the previous one no longer applies — drop it and
		// let ensureLinked() re-link against the new account.
		const switchedAccount = authRef.current && authRef.current.email !== value;

		// Register never password-gates this funnel any more. A new email gets a
		// full session; an existing one — passworded or not — gets a token good
		// for exactly this checkout (link the profile, pay with a fresh card),
		// and the purchase response upgrades it to a real session once the card
		// clears. The repeat buyer types nothing but their email.
		const reg = await axios.post('https://api.viralgpt.com/auth/register', {
			email: value,
			password: 'no-password',
			purchasePlan: plan,
			funnel: '50off',
			affiliate: getAffiliate(),
		}, { withCredentials: true });
		authRef.current = { token: reg.data.accessToken, email: value };
		if (switchedAccount) linkedRef.current = null;
		if (!reg.data.existing) firePixel('CompleteRegistration', { email: value });
		return authRef.current;
	};

	// Link the profile once a username is entered. If the visitor edits the
	// username afterwards, unlink the previous profile so only one stays linked.
	const ensureLinked = async () => {
		const user = cleanUsername(username);
		if (!user || user.includes('@') || /\.(com|net|org)$/i.test(user)) return null;

		const auth0 = authRef.current || (await ensureRegistered());
		if (!auth0) return null;
		const auth = { headers: { Authorization: `Bearer ${auth0.token}` }, withCredentials: true };

		const prev = linkedRef.current;
		if (prev && prev.username === user && prev.platform === platform) return prev;

		if (prev) {
			try {
				await axios.post('https://api.viralgpt.com/auth/unlink', { profileid: prev.profileid }, auth);
			} catch (e) { /* keep going; a stale profile shouldn't block checkout */ }
			linkedRef.current = null;
		}

		try {
			const link = await axios.post('https://api.viralgpt.com/auth/link',
				{ platform, username: user, purchasePlan: plan, funnel: '50off', affiliate: getAffiliate() }, auth);
			if (link.data.profileid) {
				await axios.post('https://api.viralgpt.com/auth/select', { profileid: link.data.profileid }, auth);
				linkedRef.current = { profileid: link.data.profileid, platform, username: user };
			}
		} catch (err) {
			const msg = err.response?.data?.error || err.response?.data?.message || '';
			// "already exists" just means this profile is already on the account.
			if (!msg.toLowerCase().includes('already exists') && !msg.toLowerCase().includes('purchase a plan')) {
				throw err;
			}
			linkedRef.current = { profileid: null, platform, username: user };
		}
		return linkedRef.current;
	};

	// Capture runs without blocking or surfacing errors — the submit path re-runs
	// it and reports failures there. Queued rather than guarded so a later call
	// isn't dropped because the previous one is still in flight.
	const queueCapture = (fn) => {
		queueRef.current = queueRef.current.then(fn).catch(() => {});
		return queueRef.current;
	};

	// Follower widget for whichever account we're about to grow. Never awaited —
	// the form renders immediately and the numbers fill in behind it.
	const fetchGain = (rawUser, plat) => {
		const user = cleanUsername(rawUser);
		if (!user) return;
		axios.get(`https://api.viralgpt.com/auth/getProfile/${plat}/${encodeURIComponent(user)}`)
			.then((res) => { if (mountedRef.current) setGainProfile(res.data); })
			.catch(() => {});
	};

	// Register then link, in that order — ensureLinked registers on its own if it
	// has to, but doing it explicitly keeps the failure surface in one place.
	const captureIdentity = () => queueCapture(async () => {
		const account = await ensureRegistered();
		if (account && cleanUsername(username)) await ensureLinked();
	});

	// Only meaningful once an account exists; before Continue there is nothing
	// to re-link.
	const relinkIfRegistered = () => {
		if (authRef.current) queueCapture(ensureLinked);
	};

	// payment is either { card, exp, cvv } (manual entry) or { paymenttoken }
	// (Apple/Google Pay via CollectJS).
	const completePurchase = async (payment) => {
		setError('');

		const user = cleanUsername(username);
		if (!email.trim()) {
			setError('Please enter your email address.');
			return;
		}
		if (!user) {
			setError(T.errUsername);
			return;
		}
		if (user.includes('@') || /\.(com|net|org)$/i.test(user)) {
			setError(T.errUsernameIsEmail(platformLabel));
			return;
		}

		setLoading(true);
		// 'submit' is the card being sent, approved or not — the same step
		// rizzup's cro logs, so approval rate is readable per arm.
		logOfferEvent('submit', { pack: plan });
		try {
			// Account and profile are normally already captured on field blur;
			// these are no-ops if nothing changed since.
			await queueRef.current;
			const account = await ensureRegistered();
			if (!account) {
				setError(T.errEmail);
				setLoading(false);
				return;
			}
			await ensureLinked();
			const token = account.token;
			const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

			// 3. Charge
			const purchaseRes = await axios.post('https://api.viralgpt.com/purchase', {
				...payment,
				newPlan: plan,
				// The tier the customer picked. The server multiplies its own base
				// price by it — the figure shown here never reaches the charge.
				quality,
				autoRefill,
				newInterval: 'monthly',
				upsellChoices: [...upsells, ...growthAddons],
				// These three offers are one-time purchases that unlock the
				// dashboard — never a recurring plan, so there's no toggle.
				recurring: false,
				funnel: '50off',
				// The offer-test arm, so the server prices the package the way this
				// page showed it. Ignored unless the arm reprices that pack.
				offerVariant: currentVariant() || undefined,
			}, auth);

			// The purchase response carries the real session: a returning buyer
			// checked out on a token scoped to this purchase alone, and the
			// cleared charge is what earns dashboard access. Falling back to the
			// register token keeps new accounts working if the field is absent.
			const sessionToken = purchaseRes.data?.accessToken || token;
			authRef.current = { ...authRef.current, token: sessionToken };
			localStorage.setItem('accessToken', sessionToken);
			setHasPassword(!!purchaseRes.data?.passwordSet);
			// Decides which add-on CTA the success screen may show. Read from the
			// payment that actually cleared, not from which button was mounted.
			setPaidWithWallet(!!payment.paymenttoken);
			firePixel('Purchase', { email: email.trim(), price: total });
			// pack is what makes the tab able to value a buyer; without it the
			// arm's value per visitor is withheld rather than guessed.
			logOfferEvent('purchase', { pack: plan, orderid: purchaseRes.data?.orderId || null });
			setSuccess(true);
			window.scrollTo(0, 0);
		} catch (err) {
			setError(err.response?.data?.error || err.response?.data?.message || T.errGeneric);
		} finally {
			setLoading(false);
		}
	};

	// How many followers this purchase actually sent: the package, plus half
	// again if they took the follower upsell. Naming the real number is what
	// makes the engagement offer land — it is engagement for THOSE followers,
	// not a generic add-on.
	const deliveredFollowers = () =>
		p.monthlyGrowth + (upsells.includes('engagement') ? Math.round(p.monthlyGrowth / 2) : 0);

	// The engagement add-on charges the card the purchase just saved, so this is
	// a bare POST — the server resolves the card from the profile.
	const buyEngagement = async (payment = {}) => {
		setEngagementError('');
		setEngagementLoading(true);
		try {
			await axios.post('https://api.viralgpt.com/purchase/engagement', payment, {
				headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
			});
			setEngagementDone(true);
			// NOT a second Purchase. Meta optimizes campaigns on the Purchase
			// count, and firing it again for the add-on made every upsell taker
			// look like two buyers — inflating conversions and halving the
			// apparent CPA. This is its own event so the count stays one per
			// buyer; index.js gives it the same currency/value block as Purchase,
			// so the revenue is still reported.
			firePixel('PurchaseUpsell', { email: email.trim(), price: postPurchaseEngagement.price });
		} catch (err) {
			setEngagementError(err.response?.data?.error || T.errGeneric);
		} finally {
			setEngagementLoading(false);
		}
	};

	// Same identity validation completePurchase runs, surfaced before we ask for
	// a card. Capture already happened on blur; this just gates the step.
	// One error surface shared by both steps.
	const errorBlock = error ? (
		<div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
			<i className="fa-solid fa-circle-exclamation" style={{ color: '#e74c3c', marginTop: 2 }}></i>
			<div className="stripe-body stripe-body--xs" style={{ color: '#b91c1c' }}>{error}</div>
		</div>
	) : null;

	// Collapse the identity editor back to the summary row and refresh the widget
	// for whatever account they settled on.
	const handleIdentityDone = () => {
		const user = cleanUsername(username);
		if (!user) {
			setError(T.errUsername);
			return;
		}
		if (user.includes('@') || /\.(com|net|org)$/i.test(user)) {
			setError(T.errUsernameIsEmail(platformLabel));
			return;
		}
		setError('');
		setEditingIdentity(false);
		setGainProfile(null);
		fetchGain(user, platform);
		relinkIfRegistered();
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		completePurchase({
			card: cardRef.current.value,
			exp: expRef.current.value,
			cvv: cvvRef.current.value,
		});
	};

	// AddPaymentInfo means the visitor supplied a usable payment method: every
	// card field complete and valid, or an authorized wallet payment. Fires once
	// per checkout regardless of which path got there.
	const emailRef = useRef(email);
	emailRef.current = email;
	const fireAddPaymentInfo = () => {
		if (paymentInfoFired.current) return;
		paymentInfoFired.current = true;
		firePixel('AddPaymentInfo', { email: emailRef.current });
	};

	// Any of the three fields can be the one that completes the set, so all three
	// re-check on every keystroke.
	const checkCardFields = () => {
		if (paymentInfoFired.current) return;
		if (cardFieldsComplete(cardRef.current?.value, expRef.current?.value, cvvRef.current?.value)) {
			fireAddPaymentInfo();
		}
	};

	// CollectJS invokes its callback from outside React, so route it through a
	// ref that always holds the latest form values.
	const walletSubmitRef = useRef();
	walletSubmitRef.current = (paymenttoken) => {
		// After the purchase the same buttons drive the engagement upsell, which
		// is a separate charge against the wallet rather than another package.
		if (success) {
			buyEngagement({ paymenttoken });
			return;
		}
		// Wallet path: the sheet has been authorized and handed us a token, which
		// is the wallet equivalent of a completed card form.
		fireAddPaymentInfo();
		completePurchase({ paymenttoken });
	};

	// Ask the server which pack this buyer earned, before anything is quoted or
	// authorized. `offerResolved` gates the wallet mount below rather than
	// `engagementOffer` itself, so a failed lookup still opens the offer on the
	// local guess instead of stranding the buyer with no way to take it.
	const [offerResolved, setOfferResolved] = useState(false);
	useEffect(() => {
		if (!success) return;
		let cancelled = false;
		axios.get('https://api.viralgpt.com/purchase/engagement/offer', {
			headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
		})
			.then((res) => { if (!cancelled) setEngagementOffer(res.data); })
			.catch((err) => { if (!cancelled) console.error('Engagement offer lookup failed:', err.message); })
			.finally(() => { if (!cancelled) setOfferResolved(true); });
		return () => { cancelled = true; };
	}, [success]);

	// The success screen mounts wallet buttons for the package-matched upsell.
	// Without this a wallet buyer has no card on file, so the one-click path
	// would have nothing to charge.
	// Waits on the resolved price: mounting at the local guess and re-mounting
	// when the server answers is the exact race that authorized a sheet at one
	// amount and charged another.
	useEffect(() => {
		if (!success || engagementDone || !offerResolved) return;
		const timeoutId = setTimeout(() => {
			if (!mountedRef.current) return;
			injectCollectJS(
				'https://secure.nmi.com/token/Collect.js',
				'9t4q59-U47k7W-Cj7Ty9-MbC6Y6',
				(token) => walletSubmitRef.current(token),
				postPurchaseEngagement.price.toFixed(2),
			).catch((error) => {
				console.error('Error initializing CollectJS for upsell:', error);
				// A card buyer still has the one-click button, so a failed mount
				// costs them nothing. A wallet buyer has no other way to pay —
				// without this they would be left looking at an offer with no
				// control on it at all.
				if (paidWithWallet) setEngagementError(T.errGeneric);
			});
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [success, engagementDone, offerResolved, postPurchaseEngagement.price, paidWithWallet, T.errGeneric]);

	// Mount Apple/Google Pay buttons on the checkout screen and re-init whenever
	// the charge amount changes (CollectJS bakes the price into the wallet sheet).
	// Mirrors the init sequence in RegisterCheckout.
	// Stops once the purchase succeeds — the success screen mounts its own
	// buttons for the upsell at a different amount, and two inits racing over
	// the same button ids would leave whichever lost holding the wrong price.
	useEffect(() => {
		if (view !== 'checkout' || success) return;

		setIsReinitializingCollect(true);

		const timeoutId = setTimeout(() => {
			if (!mountedRef.current) return;
			try {
				injectCollectJS(
					'https://secure.nmi.com/token/Collect.js',
					'9t4q59-U47k7W-Cj7Ty9-MbC6Y6',
					(token) => walletSubmitRef.current(token),
					total.toFixed(2),
				)
					.then(() => {
						if (!mountedRef.current) return;
						setIsReinitializingCollect(false);
					})
					.catch((error) => {
						if (!mountedRef.current) return;
						console.error('Error initializing CollectJS:', error);
						setIsReinitializingCollect(false);
					});
			} catch (error) {
				if (!mountedRef.current) return;
				console.error('Error calling injectCollectJS:', error);
				setIsReinitializingCollect(false);
			}
		}, 300);

		return () => clearTimeout(timeoutId);
	}, [view, total]);

	// CollectJS throws from its own script on teardown; don't let it surface.
	useEffect(() => {
		mountedRef.current = true;

		const handleError = (event) => {
			if (event.filename && event.filename.includes('nmi.com')) {
				event.preventDefault();
				console.warn('CollectJS error caught:', event.message);
				return true;
			}
		};

		window.addEventListener('error', handleError);

		return () => {
			mountedRef.current = false;
			window.removeEventListener('error', handleError);
		};
	}, []);

	const handleSetPassword = () => {
		const password = document.getElementById('fiftyoff-password')?.value || '';
		// The password is an offer, not a toll. An empty field continues to the
		// dashboard — the purchase already authenticated them — and only an
		// actual attempt at a too-short password is worth stopping for.
		if (!password) {
			window.location.href = '/dashboard/magnet';
			return;
		}
		if (password.length < 8) {
			setError(T.passwordTooShort);
			return;
		}
		axios.post('https://api.viralgpt.com/auth/settings', { oldPassword: 'no-password', newPassword: password }, {
			headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
		})
			.then(() => { window.location.href = '/dashboard/magnet'; })
			.catch(() => { window.location.href = '/dashboard/magnet'; });
	};

	const hh = String(Math.floor(secondsLeft / 3600)).padStart(2, '0');
	const mm = String(Math.floor((secondsLeft % 3600) / 60)).padStart(2, '0');
	const ss = String(secondsLeft % 60).padStart(2, '0');

	const inputStyle = {
		padding: '0.75rem 0.85rem',
		fontSize: '0.95rem',
		borderRadius: 8,
		border: '1px solid #e3e8ee',
		boxShadow: '0 1px 2px rgba(50,50,93,0.08)',
		width: '100%',
	};
	const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500 };

	// ─── SUCCESS ───
	if (success) {
		return (
			<div style={{ overflowX: 'hidden' }}>
				<Navbar hideBanner={true} ctaHref="#pricing" logoHref={cameFromVariant() ? VARIANT_SITE : "/"} />
				<div className="stripe-section stripe-section--gradient" style={{ minHeight: '80vh' }}>
					<Confetti />
					<div className="stripe-card stripe-card--elevated stripe-card--static" style={{ maxWidth: 480, margin: '0 auto', padding: '2.5rem 2rem', textAlign: 'center', background: '#fff', borderRadius: 16 }}>
						<div style={{ width: 64, height: 64, borderRadius: '50%', background: '#d4f4e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
							<i className="fa-solid fa-check" style={{ fontSize: '1.5rem', color: '#1a7f4b' }}></i>
						</div>
						<h2 className="stripe-heading stripe-heading--sm" style={{ marginBottom: 8 }}>{T.successHeading}</h2>
						<p className="stripe-body stripe-body--sm stripe-text-muted" style={{ marginBottom: 24 }}>
{T.successBody(cleanUsername(username))}
						</p>

						{/* One-click engagement add-on. The card that just paid is on
						    file, so this charges without asking for it again. Offered
						    once: taken or declined, it doesn't come back. */}
						{!engagementDone && (
							<div style={{ border: '1px solid #e3e8ee', borderRadius: 12, padding: '14px 16px', marginBottom: 16, textAlign: 'left', background: '#faf9ff' }}>
								<div style={{ fontWeight: 700, color: '#0a2540', fontSize: '0.92rem', marginBottom: 4 }}>
									{T.engagementTitle(deliveredFollowers().toLocaleString())}
								</div>
								<div style={{ color: '#68778d', fontSize: '0.8rem', marginBottom: 10 }}>
								{`Add ${postPurchaseEngagement.likes.toLocaleString()} likes and ${postPurchaseEngagement.views.toLocaleString()} views so your posts don’t look flat — spread across your recent posts.`}
								</div>
								{engagementError && (
									<div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 8 }}>{engagementError}</div>
								)}
								{/* Wallet first: a buyer who paid with Apple/Google Pay has
								    no saved card, so this is their only way to take the offer. */}
								<div style={{ marginBottom: 8 }}>
									<div id="googlepaybutton"></div>
									{window.location.hostname !== 'localhost' && <div id="applepaybutton"></div>}
								</div>
								{/* One-click is offered ONLY to card buyers. A wallet buyer
								    has nothing on file for it to charge, so showing it to
								    them put a full-width primary button on the screen whose
								    every press returned "No payment method available" — it
								    was 73% of all attempts on this offer and every one of
								    them failed. The wallet buttons above are their path. */}
								{!paidWithWallet && (
									<button
										type="button"
										onClick={() => buyEngagement()}
										disabled={engagementLoading}
										className="stripe-btn stripe-btn--primary w-100"
										style={{ padding: '0.7rem', fontSize: '0.9rem' }}
									>
									{engagementLoading ? T.processing : `Add for $${postPurchaseEngagement.price.toFixed(2)}`}
									</button>
								)}
							</div>
						)}
						{engagementDone && (
							<div style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 12, padding: '12px 16px', marginBottom: 16, color: '#166534', fontSize: '0.85rem', fontWeight: 600 }}>
								<i className="fa-solid fa-check" style={{ marginRight: 8 }}></i>{T.engagementDone}
							</div>
						)}

						{/* Only offered while there is no password to replace — a repeat
						    buyer who set one already just gets the door. */}
						{!hasPassword && (
							<input type="password" id="fiftyoff-password" className="form-control mb-3" placeholder={T.passwordPlaceholder} style={{ ...inputStyle, marginBottom: 12 }} />
						)}
						{error && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
						<button onClick={handleSetPassword} className="stripe-btn stripe-btn--primary w-100" style={{ padding: '0.85rem', fontSize: '1rem' }}>
							{T.goToDashboard}
						</button>
					</div>
				</div>
				<Footer />
			</div>
		);
	}

	return (
		<div style={{ overflowX: 'hidden' }}>
			{/* Checkout deliberately has no navbar — every link is an exit from a
			    page the visitor is one field away from converting on. A plain
			    logo keeps the brand cue without offering a way out. */}
			{view === 'checkout' ? (
				<div style={{ background: '#fff', borderBottom: '1px solid #e3e8ee', padding: '0.85rem 1rem', textAlign: 'center' }}>
					<span className="text-2xl d-inline-flex align-items-center" style={{ fontWeight: 700, letterSpacing: '-0.025em' }}>
						<img src="/images/logo-icon.png" alt="ViralGPT" style={{ height: 28, width: 28, marginRight: 8 }} />
						<span className="stripe-text-dark" style={{ fontSize: '1.15rem' }}>ViralGPT</span>
					</span>
				</div>
			) : (
				<Navbar hideBanner={true} ctaHref="#pricing" logoHref={cameFromVariant() ? VARIANT_SITE : "/"} />
			)}

			<style>{RESPONSIVE_CSS}</style>

			{/* Countdown banner */}
			<div className="fiftyoff-banner" style={{ background: 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)', color: '#fff', textAlign: 'center', padding: '0.65rem 1rem', fontWeight: 600, fontSize: '0.95rem' }}>
				<i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
				{T.bannerPrefix} <strong>{T.bannerOff}</strong> {T.bannerEndsIn} <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{hh}:{mm}:{ss}</strong>
			</div>

			{view === 'plans' && (
				<>
					{/* Hero */}
					<header className="fiftyoff-hero stripe-section--gradient" style={{ paddingTop: '2.5rem', paddingBottom: '2rem' }}>
						<div className="stripe-container px-4 text-center">
							<div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-50 via-purple-50 to-orange-50 border border-pink-300 rounded-full px-4 py-2 mb-4 shadow-sm">
								<i className="fa-solid fa-star text-yellow-500 text-sm"></i>
								<span className="text-xs md:text-sm font-medium bg-gradient-to-r from-pink-600 via-purple-600 to-orange-500 bg-clip-text text-transparent">
									{T.trustedBy}
								</span>
							</div>
							<h1 className="stripe-heading stripe-heading--xl mb-3">
{T.heroHeading}
							</h1>
							<p className="stripe-body stripe-body--lg stripe-text-muted mx-auto" style={{ maxWidth: 620, marginBottom: '0.5rem' }}>
{T.heroSub}
							</p>
						</div>
					</header>

					{/* Pricing */}
					<section id="pricing" className="stripe-section--gradient" style={{ paddingTop: '1.5rem', paddingBottom: '4rem' }}>
						<div className="stripe-container px-4">
							<div className="row justify-content-center g-4">
								{PLAN_ORDER.filter((key) => !offerTiers || offerTiers.includes(key)).map((key) => {
									const pl = PLANS[key];
									return (
										<div key={key} className="col-12 col-md-6 col-lg-4">
											<div
												className="stripe-card stripe-card--static h-100"
												style={{
													background: '#fff',
													borderRadius: 16,
													border: pl.popular ? '2px solid #635bff' : '1px solid #e3e8ee',
													boxShadow: pl.popular
														? '0 30px 60px -12px rgba(99,91,255,0.25), 0 18px 36px -18px rgba(0,0,0,0.12)'
														: '0 6px 12px -2px rgba(50,50,93,0.08), 0 3px 7px -3px rgba(0,0,0,0.06)',
													padding: '2rem 1.5rem',
													position: 'relative',
													display: 'flex',
													flexDirection: 'column',
												}}
											>
												{pl.popular && (
													<div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)', color: '#fff', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>
														{T.mostPopular}
													</div>
												)}

												<div className="text-center">
													<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f6f9fc', border: '1px solid #e3e8ee', borderRadius: 12, padding: '8px 16px', marginBottom: '1rem', color: '#0a2540', fontWeight: 700 }}>
														{pl.badge ? (<><i className="fa-solid fa-user" style={{ fontSize: '0.85rem', color: '#635bff' }}></i> {pl.badge}</>) : (<i className="fa-solid fa-briefcase" style={{ fontSize: '1rem', color: '#635bff' }}></i>)}
													</div>

													<div style={{ marginBottom: 2 }}>
														<span style={{ textDecoration: 'line-through', color: '#8898aa', fontWeight: 600, fontSize: '1.05rem', marginRight: 8 }}>${money(pl.anchor)}</span>
														<span className="stripe-heading" style={{ fontSize: '2.75rem' }}>${money(pl.price)}</span>
													</div>
													<div className="stripe-label" style={{ color: '#635bff', marginBottom: 4 }}>{pl.name}</div>
													<p className="stripe-body stripe-body--sm stripe-text-muted" style={{ marginBottom: '1.25rem' }}>
														{T.followersPerMonth(pl.followers)}
													</p>
												</div>

												<div style={{ flex: 1, marginBottom: '1.5rem' }}>
													{pl.features.map(([f, bold], i) => (
														<div key={i} className="d-flex align-items-start" style={{ gap: 10, padding: '5px 0' }}>
															<i className="fa-solid fa-check" style={{ color: '#635bff', fontSize: '0.8rem', marginTop: 5, flexShrink: 0 }}></i>
															<span className="stripe-body stripe-body--sm" style={{ fontWeight: bold ? 600 : 400, color: bold ? '#0a2540' : '#425466' }}>{f}</span>
														</div>
													))}
												</div>

												<button
													onClick={() => selectPlan(key)}
													className="stripe-btn stripe-btn--primary w-100"
													style={{ padding: '0.85rem', fontSize: '1rem', fontWeight: 600 }}
												>
													{pl.cta} <i className="fa-solid fa-arrow-right" style={{ marginLeft: 8, fontSize: '0.85rem' }}></i>
												</button>
											</div>
										</div>
									);
								})}
							</div>

							<p className="text-center stripe-body stripe-body--sm stripe-text-muted" style={{ marginTop: '2rem' }}>
								<i className="fa-solid fa-lock" style={{ marginRight: 6, color: '#1a7f4b' }}></i>
								{T.secureLine}
							</p>
						</div>
					</section>

					<GuaranteeBlock T={T} />
					<Testimonials />
					<FAQ />
					<Footer />
				</>
			)}

			{view === 'checkout' && (
				<>
					<section className="stripe-section stripe-section--gradient" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
						<div style={{ maxWidth: 480, margin: '0 auto', padding: '0 1rem' }}>
							{/* Plan header */}
							<div className="stripe-card stripe-card--elevated stripe-card--static" style={{ background: '#fff', borderRadius: 16, padding: '1.15rem 1.25rem', marginBottom: 16 }}>
								{/* No plan name here. "Core" and "Elite" are keys the buyer never
								    chose by name — they picked a follower count — so the card leads
								    with that count instead (summary[0], bolded below). Pricing lives
								    in the order summary beside the payment options; repeating it
								    here just made the visitor reconcile two numbers. */}
								{p.summary.map((line, i) => (
									<div key={i} className="d-flex align-items-start" style={{ gap: 8, padding: '2px 0' }}>
										<i className="fa-solid fa-check" style={{ color: '#1a7f4b', fontSize: '0.75rem', marginTop: 4, flexShrink: 0 }}></i>
										{/* The follower count is the offer — the rest is supporting detail. */}
										<span className="stripe-body stripe-body--sm stripe-text-body" style={i === 0 ? { fontWeight: 700, color: '#0a2540' } : undefined}>{line}</span>
									</div>
								))}

								{/* Choices carried over from the visualizer, restated so the
								    visitor sees the order they configured, not a generic one. */}
								{growthPrefs && (
									<>
										<div style={{ height: 1, background: '#e3e8ee', margin: '10px 0' }}></div>
										<div className="d-flex flex-wrap" style={{ gap: 6 }}>
											{prefChips(growthPrefs, L).map((chip) => (
												<span
													key={chip.key}
													className="d-inline-flex align-items-center"
													style={{
														gap: 5,
														padding: '5px 10px',
														borderRadius: 999,
														background: '#f6f9fc',
														border: '1px solid #e3e8ee',
														color: '#0a2540',
														fontSize: '0.75rem',
														fontWeight: 600,
													}}
												>
													<span style={{ fontSize: '0.85rem', lineHeight: 1 }}>{chip.icon}</span>
													{chip.label}
												</span>
											))}
										</div>
									</>
								)}

								{/* Follower gain folded into the plan card — the visualizer handed
								    us the username, so there is nothing to wait for. */}
								{gainProfile && (
									<>
										<div style={{ height: 1, background: '#e3e8ee', margin: '10px 0' }}></div>
										<div className="d-flex" style={{ gap: 12, animation: 'fadeSlideIn 0.35s ease both' }}>
											<img
												src={gainProfile.photo || '/images/default.png'}
												alt=""
												className="rounded-full object-cover flex-shrink-0"
												style={{ width: 44, height: 44 }}
												onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
											/>
											{/* One label per line — the gain and the new total are separate
											    facts and read badly side by side. */}
											<div style={{ flex: 1, minWidth: 0 }}>
												<div className="d-flex align-items-baseline justify-content-between" style={{ gap: 8 }}>
													<span className="stripe-body stripe-body--xs stripe-text-muted text-truncate">
														{T.gainWillGain(gainProfile.username || cleanUsername(username))}
													</span>
													{!editingIdentity && (
														<button
															type="button"
															onClick={() => setEditingIdentity(true)}
															className="stripe-body stripe-body--xs"
															style={{ background: 'none', border: 'none', color: '#635bff', fontWeight: 700, cursor: 'pointer', padding: 0, flexShrink: 0 }}
														>
															{T.changeBtn}
														</button>
													)}
												</div>
												<div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0a2540', lineHeight: 1.35 }}>
													+{p.monthlyGrowth.toLocaleString()} {T.gainFollowers}
												</div>
												<div className="stripe-body stripe-body--xs stripe-text-muted">
													{T.gainNewCount}{' '}
													<strong style={{ color: '#0a2540' }}>{((gainProfile.followers || 0) + p.monthlyGrowth).toLocaleString()}</strong>
												</div>
											</div>
										</div>
									</>
								)}

								{/* Identity lives with the plan, not the payment form. The gain row
								    above already names the account and carries its own Change, so
								    this summary is only the fallback for when that row is absent
								    (profile lookup failed or hasn't landed yet). */}
								{!editingIdentity ? (
									!gainProfile && (
									<div className="d-flex align-items-center justify-content-between" style={{ marginTop: 12, padding: '10px 12px', border: '1px solid #e3e8ee', borderRadius: 10, background: '#f6f9fc' }}>
										<span className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
											<i className={`fab ${PLATFORMS.find((pf) => pf.id === platform)?.icon}`} style={{ color: '#635bff', fontSize: '1rem', flexShrink: 0 }}></i>
											<span className="text-truncate">
												<span className="stripe-body stripe-body--xs stripe-text-muted d-block" style={{ lineHeight: 1.2 }}>{T.accountLabel}</span>
												<strong style={{ color: '#0a2540', fontSize: '0.9rem' }}>@{cleanUsername(username)}</strong>
											</span>
										</span>
										<button
											type="button"
											onClick={() => setEditingIdentity(true)}
											className="stripe-body stripe-body--xs"
											style={{ background: 'none', border: 'none', color: '#635bff', fontWeight: 700, cursor: 'pointer', padding: '4px 6px', flexShrink: 0 }}
										>
											{T.changeBtn}
										</button>
									</div>
									)
								) : (
									<>
										<div style={{ height: 1, background: '#e3e8ee', margin: '12px 0' }}></div>
										<div style={{ marginBottom: 12 }}>
											<label className="stripe-body stripe-body--xs stripe-text-muted" style={labelStyle}>{T.platformLabel}</label>
											<div className="grid grid-cols-3 gap-2">
												{PLATFORMS.map((pf) => (
													<button
														key={pf.id}
														type="button"
														onClick={() => {
															setPlatform(pf.id);
															// Re-link only if an account already exists.
															relinkIfRegistered();
														}}
														className="flex items-center justify-center py-3 px-2 rounded-xl transition-all duration-200 cursor-pointer"
														style={{
															border: platform === pf.id ? '2px solid #635bff' : '2px solid #e3e8ee',
															background: platform === pf.id ? 'rgba(99,91,255,0.05)' : '#fff',
															boxShadow: platform === pf.id ? '0 4px 16px rgba(99,91,255,0.12)' : 'none',
															transform: platform === pf.id ? 'translateY(-2px)' : 'none',
														}}
														aria-label={pf.id}
														aria-pressed={platform === pf.id}
													>
														<i className={`fab ${pf.icon} text-xl`} style={
															pf.id === 'instagram'
																? {
																	background: 'linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)',
																	WebkitBackgroundClip: 'text',
																	backgroundClip: 'text',
																	WebkitTextFillColor: 'transparent',
																	opacity: platform === pf.id ? 1 : 0.45,
																}
																: { color: platform === pf.id ? pf.color : '#8898aa' }
														}></i>
													</button>
												))}
											</div>
										</div>

										<div style={{ marginBottom: 12 }}>
											<label className="stripe-body stripe-body--xs stripe-text-muted" style={labelStyle}>{T.usernameLabel(platformLabel)}</label>
											<div className="d-flex" style={{ gap: 8 }}>
												<input
													type="text"
													required
													value={username}
													onChange={(e) => setUsername(e.target.value)}
													onBlur={relinkIfRegistered}
													placeholder={T.usernamePlaceholder}
													style={{ ...inputStyle, flex: 1, minWidth: 0 }}
												/>
												<button
													type="button"
													onClick={handleIdentityDone}
													className="stripe-btn stripe-btn--primary"
													style={{ padding: '0 1rem', fontSize: '0.85rem', flexShrink: 0 }}
												>
													{T.doneBtn}
												</button>
											</div>
										</div>
									</>
								)}

							</div>

							{/* Checkout form */}
							<div className="stripe-card stripe-card--static" style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', border: '1px solid #e3e8ee' }}>
								<h3 className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540', marginBottom: 16 }}>
									<i className="fa-solid fa-lock" style={{ color: '#1a7f4b', marginRight: 6, fontSize: '0.8rem' }}></i>
									{T.billingHeading}
								</h3>

								<form onSubmit={handleSubmit}>
									<div style={{ marginBottom: 12 }}>
										<label className="stripe-body stripe-body--xs stripe-text-muted" style={labelStyle}>{T.emailLabel}</label>
										<input
											type="email"
											required
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											// The account is created here rather than behind a Continue
											// button — captureIdentity is a no-op until the address is
											// valid, and idempotent once it is.
											onBlur={captureIdentity}
											placeholder={T.emailPlaceholder}
											style={inputStyle}
										/>
									</div>

										{/* Placed under the email rather than down by the pay button: the
										    upsell changes the charge amount, and CollectJS bakes that amount
										    into the wallet sheet at init — so it has to be decided before the
										    visitor taps Apple/Google Pay. Anywhere below those buttons is too
										    late. */}
									<div style={{ marginBottom: 12 }}>
										{p.upsellOptions.map((u) => {
											const checked = upsells.includes(u.key);
											return (
												<div
													key={u.key}
													onClick={() => toggleUpsell(u.key)}
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 10,
														cursor: 'pointer',
														marginBottom: 6,
														padding: '8px 10px',
														borderRadius: 8,
														border: '1px solid ' + (checked ? '#635bff' : '#e3e8ee'),
														background: checked ? '#faf9ff' : '#fff',
														transition: 'all 0.15s',
													}}
												>
													<span style={{ flex: 1, minWidth: 0, fontSize: '0.8rem', lineHeight: 1.35 }}>
														<span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0a2540' }}>{u.label} (+${money(u.price)})</span>
														{u.sub && (
															<>
																<br />
																<span style={{ fontSize: '0.7rem', color: '#8898aa', textWrap: 'pretty' }}>{u.sub}</span>
															</>
														)}
													</span>
													<Switch checked={checked} onChange={() => toggleUpsell(u.key)} label={u.label} />
												</div>
											);
										})}
									</div>

									{/* Order summary */}
									<div style={{ border: '1px solid #e3e8ee', borderRadius: 10, padding: '14px 16px', margin: '14px 0 16px' }}>
										{/* Discounted price on one line — the anchor is struck through
										    beside it, so the separate discount row is redundant. */}
										<div className="d-flex justify-content-between" style={{ marginBottom: 8 }}>
											<span className="stripe-body stripe-body--sm stripe-text-body">{T.planLineFollowers(p.monthlyGrowth.toLocaleString())}</span>
											<span className="d-flex align-items-baseline" style={{ gap: 6 }}>
												<span className="stripe-body stripe-body--sm" style={{ color: '#8898aa', textDecoration: 'line-through' }}>${money(tierAnchor)}</span>
												<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>${money(tierPrice)}</span>
											</span>
										</div>
										{p.upsellOptions.filter((u) => upsells.includes(u.key)).map((u) => (
											<div key={u.key} className="d-flex justify-content-between" style={{ marginBottom: 8 }}>
												<span className="stripe-body stripe-body--sm stripe-text-body">{u.lineLabel || u.label}</span>
												<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>${money(u.price)}</span>
											</div>
										))}
										{/* Add-ons chosen back on the customize screen — itemised here so
										    the total never contains an unexplained charge. */}
										{addonItems.map((a) => (
											<div key={a.key} className="d-flex justify-content-between" style={{ marginBottom: 8 }}>
												<span className="stripe-body stripe-body--sm stripe-text-body">{a.label}</span>
												<span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>${money(a.price)}</span>
											</div>
										))}
										<div style={{ height: 1, background: '#e3e8ee', margin: '12px 0' }}></div>
										<div className="d-flex justify-content-between align-items-center">
											<span className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540' }}>{T.dueToday}</span>
											<span style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0a2540' }}>${money(total)}</span>
										</div>

										{/* Auto-refill. Inside the summary box and directly under the
										    total, because a recurring charge disclosed anywhere else is
										    a disclosure the buyer can say they never saw. States the
										    amount, both dates and where to cancel — the three things a
										    dispute turns on. */}
										<div style={{ borderTop: '1px solid #e3e8ee', marginTop: 12, paddingTop: 12 }}>
											<div
												onClick={() => setAutoRefill((v) => !v)}
												style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 6 }}
											>
												<span className="stripe-body stripe-body--xs" style={{ flex: 1, minWidth: 0, color: '#0a2540', fontWeight: 600, lineHeight: 1.4 }}>
													{T.autoRefillLabel(money(REFILL_PRICE))}
												</span>
												<Switch checked={autoRefill} onChange={() => setAutoRefill((v) => !v)} label={T.autoRefillLabel(money(REFILL_PRICE))} />
											</div>
											<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ lineHeight: 1.5, fontSize: '0.7rem' }}>
												{T.autoRefillDisclosure(REFILL_TRIAL_DAYS, REFILL_CYCLE_DAYS)}
											</div>
										</div>
									</div>

									{isReinitializingCollect && (
										<div className="stripe-body stripe-body--xs stripe-text-muted" style={{ textAlign: 'center', marginTop: 12 }}>
											<i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>
											Loading payment options...
										</div>
									)}


									{/* Apple/Google Pay — the fastest path, so it leads the payment options. */}
									<div style={{ marginBottom: 12 }}>
										<div id="googlepaybutton"></div>
										{window.location.hostname !== 'localhost' && <div id="applepaybutton"></div>}
									</div>

									{/* Card fields sit under the wallet buttons — no accordion, so the
									    manual path is visible without a click. */}
									<div style={{ marginBottom: 12 }}>
										<label className="stripe-body stripe-body--xs stripe-text-muted" style={labelStyle}>{T.cardLabel}</label>
										<div style={{ position: 'relative' }}>
											<InputMask
												mask="9999 9999 9999 9999"
												maskChar=""
												placeholder="1234 1234 1234 1234"
												onChange={checkCardFields}
											>
												{(inputProps) => <input {...inputProps} ref={cardRef} type="text" required style={{ ...inputStyle, paddingRight: '3rem' }} />}
											</InputMask>
											<div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4, opacity: 0.4 }}>
												<i className="fa-brands fa-cc-visa" style={{ fontSize: '1.1rem' }}></i>
												<i className="fa-brands fa-cc-mastercard" style={{ fontSize: '1.1rem' }}></i>
												<i className="fa-brands fa-cc-amex" style={{ fontSize: '1.1rem' }}></i>
											</div>
										</div>
									</div>

									<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
										<div>
											<label className="stripe-body stripe-body--xs stripe-text-muted" style={labelStyle}>{T.expirationLabel}</label>
											<InputMask mask="99/99" maskChar="" placeholder="MM/YY" onChange={checkCardFields}>
												{(inputProps) => <input {...inputProps} ref={expRef} type="text" required style={inputStyle} />}
											</InputMask>
										</div>
										<div>
											<label className="stripe-body stripe-body--xs stripe-text-muted" style={labelStyle}>{T.cvcLabel}</label>
											<InputMask mask="9999" maskChar="" placeholder="CVC" onChange={checkCardFields}>
												{(inputProps) => <input {...inputProps} ref={cvvRef} type="text" required style={inputStyle} />}
											</InputMask>
										</div>
									</div>

									{errorBlock}

									<button type="submit" disabled={loading || isReinitializingCollect} className="stripe-btn stripe-btn--primary w-100" style={{ padding: '0.85rem', fontSize: '1rem', marginTop: 20, opacity: (loading || isReinitializingCollect) ? 0.6 : 1 }}>
										{loading ? (<><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }}></i>{T.processing}</>) : (<>{T.startMyGrowth} <i className="fa-solid fa-arrow-right" style={{ marginLeft: 8 }}></i></>)}
									</button>


								</form>
							</div>

							{/* Trust footer */}
							<div style={{ padding: '18px 0 0', textAlign: 'center' }}>
								<div className="d-flex align-items-center justify-content-center flex-wrap" style={{ gap: 16, color: '#8898aa', fontSize: '0.78rem', marginBottom: 10 }}>
									<span><i className="fa-solid fa-lock" style={{ fontSize: '0.65rem', color: '#1a7f4b', marginRight: 5 }}></i>{T.secureCheckout}</span>
								</div>
								<div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
									<i className="fa-brands fa-cc-visa" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
									<i className="fa-brands fa-cc-mastercard" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
									<i className="fa-brands fa-cc-amex" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
									<i className="fa-brands fa-cc-discover" style={{ fontSize: '1.4rem', color: '#8898aa' }}></i>
								</div>
							</div>
						</div>
					</section>

					{/* Checkout ends on the guarantee — nothing below it to scroll to. */}
					<GuaranteeBlock T={T} />
				</>
			)}
		</div>
	);
};

export default FiftyOff;
